import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useProject } from '../api/queries';
import {
  useCreateProject,
  useProjectSetup,
  useSaveSetup,
  type CreateProjectInput,
  type ProjectSetup,
  type SetupEnvelope,
  type SourceConnection,
} from '../api/setup';
import { Field } from '../components/Form';
import { EmptyState, ErrorState, Loading } from '../components/States';
import { newReference } from '../lib/events';
import {
  LIMITS,
  SERVICES,
  draftFromSetup,
  emptyDraft,
  firstInvalidStep,
  newDestination,
  newSource,
  sameSetup,
  stepErrors,
  toSetup,
  type Draft,
  type Errors,
  type Mode,
} from '../lib/setup';
import { PILOTS } from './Pilots';

export const STEPS = ['Project and goal', 'Sources', 'Destinations', 'Review and save'] as const;
const LAST = STEPS.length - 1;

type Refresh = 'refreshing' | 'refreshed' | 'failed';

/** Only the service can report a connection; a saved link alone is never shown as connected. */
function ConnectionState({ connection }: { connection: SourceConnection | undefined }) {
  if (connection?.status === 'connected') return <span className="tag tag--ok tag--wrap">Connected (reported by the service)</span>;
  return <span className="tag tag--warn tag--wrap">Not connected</span>;
}

function Stepper({ current, reached, onJump }: { current: number; reached: number; onJump: (step: number) => void }) {
  return (
    <ol className="steps" aria-label="Setup steps">
      {STEPS.map((label, index) => (
        <li key={label} className={index === current ? 'steps__item steps__item--current' : 'steps__item'}>
          {index < current && index <= reached ? (
            <button type="button" className="steps__button" onClick={() => onJump(index)}>
              <span className="steps__n">{index + 1}</span> {label}
            </button>
          ) : (
            <span className="steps__label" aria-current={index === current ? 'step' : undefined}>
              <span className="steps__n">{index + 1}</span> {label}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

/** The complete brief as it will be saved. */
function BriefSummary({ draft, connections, showTitle }: { draft: Draft; connections: SourceConnection[]; showTitle: boolean }) {
  const setup = toSetup(draft);
  return (
    <dl className="brief">
      {showTitle && (
        <div>
          <dt>Project</dt>
          <dd>{draft.title.trim()}</dd>
        </div>
      )}
      <div>
        <dt>Goal</dt>
        <dd>{setup.goal}</dd>
      </div>
      <div>
        <dt>Useful progress</dt>
        <dd>{setup.successSignal ?? <span className="muted">Not described</span>}</dd>
      </div>
      <div>
        <dt>Sources</dt>
        <dd>
          {setup.sources.length === 0 ? (
            <span className="muted">None yet</span>
          ) : (
            <ul className="brief__list">
              {setup.sources.map((source) => (
                <li key={source.id}>
                  <strong>{source.label}</strong> ({SERVICES.find((service) => service.value === source.service)?.label}) — {source.purpose}{' '}
                  <ConnectionState connection={connections.find((connection) => connection.sourceId === source.id)} />
                  {source.reference !== undefined && <span className="brief__ref">{source.reference}</span>}
                </li>
              ))}
            </ul>
          )}
        </dd>
      </div>
      <div>
        <dt>Destinations</dt>
        <dd>
          {setup.destinations.length === 0 ? (
            <span className="muted">None yet</span>
          ) : (
            <ul className="brief__list">
              {setup.destinations.map((destination) => (
                <li key={destination.id}>
                  <strong>{destination.title}</strong> — {destination.purpose}. Belongs here: {destination.expectedContent}
                  {destination.reference !== undefined && <span className="brief__ref">{destination.reference}</span>}
                </li>
              ))}
            </ul>
          )}
        </dd>
      </div>
    </dl>
  );
}

/** What happens after setup, stated plainly: nothing automated runs yet. */
function WhatHappensNext() {
  return (
    <aside className="panel panel--loop" aria-labelledby="next-title">
      <h3 id="next-title" className="minor-title">
        What happens next
      </h3>
      <p className="small">
        Later, AI will use this brief to draft project updates, suggest where each one should go, and point out help worth asking for and a
        next step. Nothing is shared until you review and approve it. This isn’t switched on yet: saving only keeps your brief.
      </p>
    </aside>
  );
}

interface WizardProps {
  mode: Mode;
  projectTitle?: string;
  draft: Draft;
  onChange: (draft: Draft) => void;
  step: number;
  onStep: (step: number) => void;
  locked: boolean;
  connections: SourceConnection[];
  saveArea: ReactNode;
}

function Wizard({ mode, projectTitle, draft, onChange, step, onStep, locked, connections, saveArea }: WizardProps) {
  const [errors, setErrors] = useState<Errors>({});
  const [reached, setReached] = useState(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    heading.current?.focus();
  }, [step]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => onChange({ ...draft, [key]: value });
  const setSource = (index: number, patch: Partial<Draft['sources'][number]>) =>
    set('sources', draft.sources.map((source, position) => (position === index ? { ...source, ...patch } : source)));
  const setDestination = (index: number, patch: Partial<Draft['destinations'][number]>) =>
    set('destinations', draft.destinations.map((destination, position) => (position === index ? { ...destination, ...patch } : destination)));

  function go(to: number) {
    if (to > step) {
      const found = stepErrors(step, draft, mode);
      setErrors(found);
      if (Object.keys(found).length > 0) return;
      setReached((current) => Math.max(current, to));
    } else {
      setErrors({});
    }
    onStep(to);
  }

  return (
    <div className="wizard">
      <Stepper current={step} reached={locked ? -1 : reached} onJump={go} />
      <section className="panel wizard__step" aria-labelledby="step-title">
        <p className="eyebrow">
          Step {step + 1} of {STEPS.length}
        </p>
        <h2 id="step-title" ref={heading} tabIndex={-1} className="panel__title">
          {STEPS[step]}
        </h2>
        {Object.keys(errors).length > 0 && (
          <p className="form-error" role="alert">
            Some fields need attention before you continue.
          </p>
        )}
        <fieldset className="wizard__fields" disabled={locked}>
          <legend className="visually-hidden">{STEPS[step]}</legend>
          {step === 0 && (
            <>
              {mode === 'create' ? (
                <Field label="Project name" error={errors.title ?? null}>
                  {(props) => <input {...props} className="input" maxLength={LIMITS.title} value={draft.title} onChange={(event) => set('title', event.target.value)} />}
                </Field>
              ) : (
                <p>
                  <span className="field__label">Project</span> <strong>{projectTitle}</strong>
                </p>
              )}
              {mode === 'create' && (
                <Field label="Short description (optional)">
                  {(props) => <input {...props} className="input" maxLength={LIMITS.summary} value={draft.summary} onChange={(event) => set('summary', event.target.value)} />}
                </Field>
              )}
              <Field label="Goal" help="What do you want to learn, solve or change? This guides what is gathered and suggested later." error={errors.goal ?? null}>
                {(props) => <textarea {...props} className="input textarea" rows={4} maxLength={LIMITS.goal} value={draft.goal} onChange={(event) => set('goal', event.target.value)} />}
              </Field>
              <Field label="What would useful progress look like? (optional)" help="In your words. No numeric target is needed, and this is not a verified result.">
                {(props) => (
                  <textarea {...props} className="input textarea" rows={3} maxLength={LIMITS.successSignal} value={draft.successSignal} onChange={(event) => set('successSignal', event.target.value)} />
                )}
              </Field>
            </>
          )}

          {step === 1 && (
            <>
              <p className="small">
                Optional. Add the repository or other places this project’s evidence will come from, and why. A saved reference is not a
                connection: nothing is signed in to or read from it. You can skip this step and add sources later.
              </p>
              {draft.sources.length === 0 ? (
                <p className="muted">No sources yet.</p>
              ) : (
                <ol className="setup-rows" aria-label="Sources">
                  {draft.sources.map((source, index) => {
                    const n = String(index + 1);
                    return (
                      <li key={source.id} className="setup-row">
                        <p className="setup-row__head">
                          <strong>Source {n}</strong> <ConnectionState connection={connections.find((connection) => connection.sourceId === source.id)} />
                        </p>
                        <Field label={`Service for source ${n}`}>
                          {(props) => (
                            <select
                              {...props}
                              className="input"
                              value={source.service}
                              onChange={(event) => setSource(index, { service: event.target.value as (typeof SERVICES)[number]['value'] })}
                            >
                              {SERVICES.map((service) => (
                                <option key={service.value} value={service.value}>
                                  {service.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </Field>
                        <Field label={`Name for source ${n}`} error={errors[`sources.${String(index)}.label`] ?? null}>
                          {(props) => <input {...props} className="input" maxLength={LIMITS.label} value={source.label} onChange={(event) => setSource(index, { label: event.target.value })} />}
                        </Field>
                        <Field label={`Link for source ${n} (optional)`} error={errors[`sources.${String(index)}.reference`] ?? null}>
                          {(props) => (
                            <input {...props} className="input" type="url" maxLength={LIMITS.reference} value={source.reference} onChange={(event) => setSource(index, { reference: event.target.value })} />
                          )}
                        </Field>
                        <Field label={`Purpose of source ${n}`} error={errors[`sources.${String(index)}.purpose`] ?? null}>
                          {(props) => (
                            <textarea {...props} className="input textarea" rows={2} maxLength={LIMITS.purpose} value={source.purpose} onChange={(event) => setSource(index, { purpose: event.target.value })} />
                          )}
                        </Field>
                        <button
                          type="button"
                          className="button button--link"
                          onClick={() => set('sources', draft.sources.filter((_, position) => position !== index))}
                        >
                          Remove source {n}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
              <button
                type="button"
                className="button button--secondary"
                disabled={draft.sources.length >= LIMITS.entries}
                onClick={() => set('sources', [...draft.sources, newSource()])}
              >
                Add a source
              </button>
              {draft.sources.length >= LIMITS.entries && <p className="small muted">A project can list up to {LIMITS.entries} sources.</p>}
            </>
          )}

          {step === 2 && (
            <>
              <p className="small">
                Where updates for this project could go: project or topic threads. These are intended destinations only; saving them grants
                no permission to read or post anywhere.
              </p>
              {draft.destinations.length === 0 ? (
                <p className="muted">No destinations yet.</p>
              ) : (
                <ol className="setup-rows" aria-label="Destinations">
                  {draft.destinations.map((destination, index) => {
                    const n = String(index + 1);
                    return (
                      <li key={destination.id} className="setup-row">
                        <p className="setup-row__head">
                          <strong>Destination {n}</strong>
                        </p>
                        <Field label={`Title of destination ${n}`} error={errors[`destinations.${String(index)}.title`] ?? null}>
                          {(props) => (
                            <input {...props} className="input" maxLength={LIMITS.label} value={destination.title} onChange={(event) => setDestination(index, { title: event.target.value })} />
                          )}
                        </Field>
                        <Field label={`Purpose of destination ${n}`} error={errors[`destinations.${String(index)}.purpose`] ?? null}>
                          {(props) => (
                            <textarea
                              {...props}
                              className="input textarea"
                              rows={2}
                              maxLength={LIMITS.purpose}
                              value={destination.purpose}
                              onChange={(event) => setDestination(index, { purpose: event.target.value })}
                            />
                          )}
                        </Field>
                        <Field label={`What belongs in destination ${n}`} error={errors[`destinations.${String(index)}.expectedContent`] ?? null}>
                          {(props) => (
                            <textarea
                              {...props}
                              className="input textarea"
                              rows={2}
                              maxLength={LIMITS.purpose}
                              value={destination.expectedContent}
                              onChange={(event) => setDestination(index, { expectedContent: event.target.value })}
                            />
                          )}
                        </Field>
                        <Field label={`Link for destination ${n} (optional)`} error={errors[`destinations.${String(index)}.reference`] ?? null}>
                          {(props) => (
                            <input
                              {...props}
                              className="input"
                              type="url"
                              maxLength={LIMITS.reference}
                              value={destination.reference}
                              onChange={(event) => setDestination(index, { reference: event.target.value })}
                            />
                          )}
                        </Field>
                        <button
                          type="button"
                          className="button button--link"
                          onClick={() => set('destinations', draft.destinations.filter((_, position) => position !== index))}
                        >
                          Remove destination {n}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
              <button
                type="button"
                className="button button--secondary"
                disabled={draft.destinations.length >= LIMITS.entries}
                onClick={() => set('destinations', [...draft.destinations, newDestination()])}
              >
                Add a destination
              </button>
              {draft.destinations.length >= LIMITS.entries && <p className="small muted">A project can list up to {LIMITS.entries} destinations.</p>}
            </>
          )}

          {step === LAST && (
            <>
              <BriefSummary draft={draft} connections={connections} showTitle={mode === 'create'} />
              {mode === 'create' && (
                <fieldset className="choice-group">
                  <legend className="field__label">Who can see this project</legend>
                  <label className={`choice${draft.audience === 'private' ? ' choice--selected' : ''}`}>
                    <input type="radio" name="audience" checked={draft.audience === 'private'} onChange={() => set('audience', 'private')} />
                    <span className="choice__text">
                      <span className="choice__label">Only me</span>
                      <span className="choice__help">Private to you. This is the default.</span>
                    </span>
                  </label>
                  <label className={`choice${draft.audience === 'members' ? ' choice--selected' : ''}`}>
                    <input type="radio" name="audience" checked={draft.audience === 'members'} onChange={() => set('audience', 'members')} />
                    <span className="choice__text">
                      <span className="choice__label">Invited members</span>
                      <span className="choice__help">
                        Other invited members can see the project’s name and description. Its setup is seen only by you and the hub’s owner,
                        who is authorized to open and change it.
                      </span>
                    </span>
                  </label>
                  <p className="small muted">Who can see a project can’t be changed after it is created in this first version.</p>
                </fieldset>
              )}
              <WhatHappensNext />
            </>
          )}
        </fieldset>
        {step === LAST && saveArea}
        <div className="button-row wizard__nav">
          {step > 0 && (
            <button type="button" className="button button--secondary" disabled={locked} onClick={() => go(step - 1)}>
              Back
            </button>
          )}
          {step < LAST && (
            <button type="button" className="button button--primary" disabled={locked} onClick={() => go(step + 1)}>
              Next: {STEPS[step + 1]}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

type CreateOutcome =
  | { kind: 'unclear'; message: string }
  | { kind: 'refused'; code: string; message: string }
  // A refused resend after an unclear attempt: the first attempt may still have created the project.
  | { kind: 'uncertain'; code: string; message: string }
  | null;

/** /projects/new — any invited member may start their own project. */
export function NewProject() {
  const create = useCreateProject();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [step, setStep] = useState(0);
  const [attemptKey, setAttemptKey] = useState(newReference);
  const [sent, setSent] = useState<CreateProjectInput | null>(null);
  const [outcome, setOutcome] = useState<CreateOutcome>(null);
  const [created, setCreated] = useState<{ id: string | null; audience: CreateProjectInput['audience'] } | null>(null);
  // Held: the exact input and request ID stay frozen until an answer is known, or the member deliberately starts over.
  const held = outcome?.kind === 'unclear' || outcome?.kind === 'uncertain';

  function startOver() {
    setOutcome(null);
    setSent(null);
    setAttemptKey(newReference());
  }

  function submit(input: CreateProjectInput, afterUnclear: boolean) {
    setSent(input);
    create.mutate(input, {
      onSuccess: (result) => {
        setOutcome(null);
        setCreated({ id: result.project.id, audience: input.audience });
      },
      onError: (failure) => {
        if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500 && afterUnclear) {
          // Refusing the resend says nothing about the first attempt: keep the same input and key.
          setOutcome({ kind: 'uncertain', code: failure.code, message: failure.message });
          return;
        }
        if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500) {
          // Definitive for a first attempt: nothing was created. A later attempt is a new request with a new key.
          const message =
            failure.code === 'PROJECT_CREATE_CONFLICT'
              ? 'this request ID was already used with different input. Check your projects before starting again.'
              : failure.message;
          setOutcome({ kind: 'refused', code: failure.code, message });
          setSent(null);
          setAttemptKey(newReference());
          return;
        }
        setOutcome({ kind: 'unclear', message: failure instanceof ApiError ? failure.message : 'No answer was received.' });
      },
    });
  }

  function createFromDraft() {
    const problem = firstInvalidStep(draft, 'create');
    if (problem !== null) {
      setStep(problem.step);
      return;
    }
    submit({
      title: draft.title.trim(),
      ...(draft.summary.trim() !== '' ? { summary: draft.summary.trim() } : {}),
      audience: draft.audience,
      setup: toSetup(draft),
      idempotencyKey: attemptKey,
    }, false);
  }

  if (created !== null) {
    return (
      <main id="main" tabIndex={-1} className="page">
        <div className="panel" role="status">
          <h1 className="panel__title">Project created</h1>
          <p>
            {created.audience === 'private'
              ? 'It is private to you.'
              : 'Invited members can see it; its setup is seen only by you and the hub’s owner.'}{' '}
            No project update was posted.
          </p>
          {created.id !== null && (
            <p className="link-list">
              <Link className="button button--secondary" to={`/projects/${encodeURIComponent(created.id)}`}>
                Open the project page
              </Link>
              <Link className="button button--secondary" to={`/projects/${encodeURIComponent(created.id)}/setup`}>
                Review its setup
              </Link>
            </p>
          )}
        </div>
      </main>
    );
  }

  const saveArea = (
    <div className="wizard__save">
      {outcome?.kind === 'refused' && (
        <p className="form-error" role="alert">
          The project was not created ({outcome.code}): {outcome.message}
        </p>
      )}
      {outcome?.kind === 'uncertain' && (
        <div className="notice notice--flag" role="alert">
          <p>
            The service refused the resend ({outcome.code}): {outcome.message} That doesn’t show whether your first attempt created the
            project, so your input and request ID are kept. Sending the same request again returns the original project if it was created.
          </p>
          <p className="small">
            Changing the details starts a new request, which could create a second project if the first one exists.{' '}
            <button type="button" className="button button--link" onClick={startOver}>
              Change the details anyway
            </button>
          </p>
        </div>
      )}
      {outcome?.kind === 'unclear' && (
        <div className="notice notice--flag" role="alert">
          <p>
            No clear answer came back ({outcome.message}). The project may already have been created, so your input is held unchanged.
            Sending the same request again is safe: it carries the same request ID, so the service returns the original project instead of
            creating another.
          </p>
        </div>
      )}
      <button
        type="button"
        className="button button--primary"
        disabled={create.isPending}
        onClick={() => (held && sent !== null ? submit(sent, true) : createFromDraft())}
      >
        {create.isPending ? 'Creating…' : held ? 'Send the same request again' : 'Create project'}
      </button>
    </div>
  );

  return (
    <main id="main" tabIndex={-1} className="page">
      <header className="page__header">
        <p className="eyebrow">New project</p>
        <h1 className="page__title">Start with a goal</h1>
        <p className="lede">
          Name the project and say what you want to learn, solve or change. Sources and destinations are optional and can come later. You
          don’t need a project or a goal just to follow others.
        </p>
      </header>
      <Wizard mode="create" draft={draft} onChange={setDraft} step={step} onStep={setStep} locked={create.isPending || held} connections={[]} saveArea={saveArea} />
    </main>
  );
}

type SaveState =
  | { kind: 'saved'; revision: number; confirmedLater: boolean }
  | { kind: 'refused'; code: string; message: string }
  | { kind: 'conflict'; refresh: Refresh; latest: SetupEnvelope | null }
  | { kind: 'unclear'; refresh: Refresh; message: string; attempted: ProjectSetup; attemptedFrom: number }
  | null;

/** /projects/:id/setup — the service decides who may edit (canEdit); others read or are refused. */
export function EditProjectSetup() {
  const { id = '' } = useParams();
  // Keyed by project: a draft, base revision, step or save state from one project never carries into another.
  return <ProjectSetupEditor key={id} id={id} />;
}

function ProjectSetupEditor({ id }: { id: string }) {
  const project = useProject(id);
  // The service decides who manages a project; nobody else is sent to its setup.
  const setupQuery = useProjectSetup(id, project.isSuccess && project.data.canManage !== false);
  const save = useSaveSetup(id);
  const pilot = PILOTS.find((candidate) => candidate.id === id);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [base, setBase] = useState(0);
  const [step, setStep] = useState(0);
  const [state, setState] = useState<SaveState>(null);

  if (draft === null && setupQuery.data !== undefined && project.data !== undefined) {
    setDraft(draftFromSetup(project.data.title, setupQuery.data, project.data.pilot?.repositoryUrl ?? pilot?.repository?.href));
    setBase(setupQuery.data.revision);
  }

  async function reconcileAfterUnclear(attempted: ProjectSetup, attemptedFrom: number, message: string) {
    setState({ kind: 'unclear', refresh: 'refreshing', message, attempted, attemptedFrom });
    const result = await setupQuery.refetch();
    if (!result.isSuccess || result.isError) {
      setState({ kind: 'unclear', refresh: 'failed', message, attempted, attemptedFrom });
      return;
    }
    const latest = result.data;
    if (latest.revision > attemptedFrom && sameSetup(latest.setup, attempted)) {
      setBase(latest.revision);
      setState({ kind: 'saved', revision: latest.revision, confirmedLater: true });
    } else if (latest.revision === attemptedFrom) {
      setState({ kind: 'unclear', refresh: 'refreshed', message, attempted, attemptedFrom });
    } else {
      setState({ kind: 'conflict', refresh: 'refreshed', latest });
    }
  }

  async function loadConflict() {
    setState({ kind: 'conflict', refresh: 'refreshing', latest: null });
    const result = await setupQuery.refetch();
    setState(result.isSuccess && !result.isError ? { kind: 'conflict', refresh: 'refreshed', latest: result.data } : { kind: 'conflict', refresh: 'failed', latest: null });
  }

  function send(expectedRevision: number) {
    if (draft === null) return;
    const problem = firstInvalidStep(draft, 'edit');
    if (problem !== null) {
      setStep(problem.step);
      return;
    }
    const attempted = toSetup(draft);
    save.mutate(
      { expectedRevision, setup: attempted },
      {
        onSuccess: (envelope) => {
          setBase(envelope.revision);
          setState({ kind: 'saved', revision: envelope.revision, confirmedLater: false });
        },
        onError: (failure) => {
          if (failure instanceof ApiError && failure.isConflict) {
            void loadConflict();
            return;
          }
          if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500) {
            setState({ kind: 'refused', code: failure.code, message: failure.message });
            return;
          }
          void reconcileAfterUnclear(attempted, expectedRevision, failure instanceof ApiError ? failure.message : 'No answer was received.');
        },
      },
    );
  }

  const title = project.data?.title ?? pilot?.title ?? 'Project';
  // Setup is 404 for anyone but its manager, even on a project they can read.
  const denied =
    project.data?.canManage === false ||
    (setupQuery.error instanceof ApiError && (setupQuery.error.isForbidden || setupQuery.error.isNotFound)) ||
    setupQuery.data?.canEdit === false;

  let body: ReactNode = null;
  if (project.isPending) body = <Loading label="Loading the project setup" />;
  else if (project.isError) body = <ErrorState error={project.error} onRetry={() => void project.refetch()} />;
  else if (denied) {
    body = (
      <EmptyState title="Only this project’s manager can change its setup">
        <p>You can still follow the project and read what is shared with you. Following needs no goal of your own.</p>
        <p>
          <Link to={`/projects/${encodeURIComponent(id)}`}>Open the project page</Link>
        </p>
      </EmptyState>
    );
  } else if (setupQuery.isPending) body = <Loading label="Loading the project setup" />;
  else if (setupQuery.isError) body = <ErrorState error={setupQuery.error} onRetry={() => void setupQuery.refetch()} />;
  else if (draft !== null && setupQuery.data !== undefined) {
    const locked = save.isPending || (state?.kind === 'unclear' && state.refresh !== 'refreshed');
    const saveArea = (
      <div className="wizard__save">
        {state?.kind === 'saved' && (
          <p className="form-success" role="status">
            Saved as revision {state.revision}
            {state.confirmedLater ? ' (confirmed by checking the latest saved setup)' : ''}. Nothing was posted or shared.
          </p>
        )}
        {state?.kind === 'refused' && (
          <p className="form-error" role="alert">
            Not saved ({state.code}): {state.message}
          </p>
        )}
        {state?.kind === 'conflict' && (
          <div className="notice notice--flag" role="alert">
            <p>Someone saved a newer revision after you started. Nothing was overwritten, and your draft is kept.</p>
            {state.refresh === 'refreshing' && <p className="small">Loading the latest saved setup…</p>}
            {state.refresh === 'failed' && (
              <p className="small">
                The latest setup could not be loaded.{' '}
                <button type="button" className="button button--link" onClick={() => void loadConflict()}>
                  Try loading it again
                </button>
              </p>
            )}
            {state.refresh === 'refreshed' && state.latest !== null && (
              <>
                <p className="small">
                  Latest saved: revision {state.latest.revision}, goal “{state.latest.setup?.goal ?? 'not set'}”.
                </p>
                <div className="button-row">
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={save.isPending}
                    onClick={() => {
                      if (state.latest === null) return;
                      setBase(state.latest.revision);
                      send(state.latest.revision);
                    }}
                  >
                    Save my draft as revision {state.latest.revision + 1}
                  </button>
                  <button
                    type="button"
                    className="button button--secondary"
                    onClick={() => {
                      if (state.latest === null) return;
                      setDraft(draftFromSetup(title, state.latest, project.data?.pilot?.repositoryUrl ?? pilot?.repository?.href));
                      setBase(state.latest.revision);
                      setState(null);
                    }}
                  >
                    Discard my draft and load revision {state.latest.revision}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {state?.kind === 'unclear' && (
          <div className="notice notice--flag" role="alert">
            <p>No clear answer came back ({state.message}). Your draft is held unchanged while the latest saved setup is checked.</p>
            {state.refresh === 'refreshing' && <p className="small">Checking the latest saved setup…</p>}
            {state.refresh === 'failed' && (
              <p className="small">
                The latest saved setup could not be loaded, so it can’t be confirmed yet.{' '}
                <button type="button" className="button button--link" onClick={() => void reconcileAfterUnclear(state.attempted, state.attemptedFrom, state.message)}>
                  Check again
                </button>
              </p>
            )}
            {state.refresh === 'refreshed' && <p className="small">It was not saved: revision {state.attemptedFrom} is still the latest. You can save again.</p>}
          </div>
        )}
        {state?.kind !== 'conflict' && (
          <button type="button" className="button button--primary" disabled={locked} onClick={() => send(base)}>
            {save.isPending ? 'Saving…' : `Save setup${base === 0 ? '' : ` (revision ${base + 1})`}`}
          </button>
        )}
      </div>
    );
    body = (
      <Wizard
        mode="edit"
        projectTitle={title}
        draft={draft}
        onChange={setDraft}
        step={step}
        onStep={setStep}
        locked={locked}
        connections={setupQuery.data.connections ?? []}
        saveArea={saveArea}
      />
    );
  }

  return (
    <main id="main" tabIndex={-1} className="page">
      <nav aria-label="Breadcrumb" className="breadcrumb">
        <ol>
          <li>
            <Link to={`/projects/${encodeURIComponent(id)}`}>{title}</Link>
          </li>
        </ol>
      </nav>
      <header className="page__header">
        <p className="eyebrow">Project setup</p>
        <h1 className="page__title">{title}</h1>
        <p className="lede">
          A saved brief guides what is gathered and suggested for this project later. Setup is revised, never overwritten silently.
        </p>
      </header>
      {body}
    </main>
  );
}
